import * as React from 'react'
import { View, Text, ScrollView } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'

import DraftIncidentCard from '@components/drafts/DraftIncidentCard'
import DraftEDLCard from '@screens/edl/list/cards/FormCard'
import DraftControleCard from '@components/drafts/DraftControleCard'

import { canUserCreateIncident } from '@screens/incident/utils'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as IncidentStore from '@store/incident/incident'
import * as SiteStore from '@store/site/site'
import * as LocationStore from '@store/location/location'
import * as EDLStore from '@store/edl/edl'
import * as ControleStore from '@store/controle/controle'
import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'

import useWorkflow from '@screens/controle/form/useWorkflow'
import { AuthScope } from '@api/enum'

import { createEvent, createStore } from 'effector'

const tabs = ['incident', 'edl', 'controle'] as const
const setTab = createEvent('setTab')
const TabStore = createStore(tabs[0]).on(setTab, (_, next) => next)

const DraftsScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const tab = useReducer(TabStore, (s) => s)

  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const manifestation = useReducer(SiteStore.store, (s) => s.manifestation)

  const incidentDrafts = useReducer(IncidentStore.store, (s) => s.forms)
  const edlDrafts = useReducer(EDLStore.store, (s) => s.forms)
  const controleDrafts = useReducer(ControleStore.store, (s) => s.forms)

  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const allManifestations = [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed]
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const allTenants = [...tenants.current, ...tenants.former, ...tenants.incoming]
  const places = useReducer(LocationStore.store, (s) => s.places)

  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const controleForm = useReducer(ControleStore.store, (s) => s.form)
  const user = useReducer(UserStore.store, (s) => s.user!)
  const [, starter] = useWorkflow(controleForm, user, {})

  const tabOptions = tabs
    .filter((t) => (canUserCreateIncident(user, manifestation) ? true : t !== 'incident'))
    .filter((t) =>
      scopes.includes(AuthScope.EdlEntreeCreation) || scopes.includes(AuthScope.EdlSortieCreation) ? true : t !== 'edl'
    )
    .filter((t) =>
      scopes.includes(AuthScope.ControleQualiteSiteCreation) ||
      scopes.includes(AuthScope.ControleQualitePrestataireCreation)
        ? true
        : t !== 'controle'
    )
    .map((t) => ({ label: i18n.t(`screens.drafts.tabs.${t}`), value: t }))

  const onStartIncident = (item: Partial<IncidentForm>) => {
    IncidentStore.actions.resetForm()

    // already started, we set current
    IncidentStore.actions.setForm(item)

    // return to last visited step
    navigation.push(`/incident/${item.step ? item.step.toLowerCase() : 'manif'}`)
  }

  const onStartEDL = (type: EtatDesLieuxTypeEnum, id: string, manif?: ManifestationResponse, tenant?: Tenant) => {
    navigation.push(`/edl/spaces/${id}?type=${type}`, {
      manifestationId: manif?.id,
      tenantId: tenant?.tenant.id,
    })
  }

  const onStartControle = (item: Partial<ControleForm>) => {
    ControleStore.actions.resetForm()

    // already started, we set current
    ControleStore.actions.setForm({ ...starter, ...item })

    // return to last visited step, or go to first step
    navigation.push(`/controles/form`)
  }

  const renderIncident = (incident: Partial<IncidentForm>) => (
    <FormContainer key={incident.id}>
      <DraftIncidentCard item={incident} onClick={(item) => onStartIncident(item)} />
    </FormContainer>
  )

  const renderEDL = (edl: EDLForm) => {
    const manif = allManifestations.find((m) => m.id === edl.manifestationId)
    const tenant = allTenants.find((t) => t.tenant.id === edl.locatairePermanentId)
    return (
      <FormContainer key={edl.id}>
        <DraftEDLCard
          form={edl}
          manifestation={manif}
          tenant={tenant}
          places={places}
          onClick={() => onStartEDL(edl.type, edl.id, manif, tenant)}
        />
      </FormContainer>
    )
  }

  const renderControle = (controle: Partial<ControleForm>) => (
    <FormContainer key={controle.id}>
      <DraftControleCard item={controle} onClick={(item) => onStartControle(item)} />
    </FormContainer>
  )

  const renderIncidents = () => {
    if (incidentDrafts.length > 0) {
      return <List key="incidents">{incidentDrafts.map(renderIncident)}</List>
    }
    return <MissingInfo>{i18n.t('screens.drafts.noDraft')}</MissingInfo>
  }

  const renderEDLs = () => {
    if (edlDrafts.length > 0) {
      return <List key="edls">{edlDrafts.map(renderEDL)}</List>
    }
    return <MissingInfo>{i18n.t('screens.drafts.noDraft')}</MissingInfo>
  }

  const renderControles = () => {
    if (controleDrafts.length > 0) {
      return <List key="inProgress">{controleDrafts.map(renderControle)}</List>
    }
    return <MissingInfo>{i18n.t('screens.drafts.noDraft')}</MissingInfo>
  }

  return (
    <Root header={i18n.t('screens.drafts.title')} fixed onLeft={navigation.goBack}>
      {!isPhone && <Header title={i18n.t('screens.drafts.title')} />}

      {tabOptions.length > 0 && (
        <SegmentedContainer>
          <SegmentedControl
            data={tabOptions}
            value={tabOptions.find((t) => t.value === tab)}
            onItemSelected={(t) => setTab(t.value)}
          />
        </SegmentedContainer>
      )}

      <ScrollContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
        {tabOptions.length === 0 && <MissingInfo>{i18n.t('screens.drafts.noDraft')}</MissingInfo>}
        {tab === 'incident' ? renderIncidents() : tab === 'edl' ? renderEDLs() : renderControles()}
      </ScrollContainer>
    </Root>
  )
}

export default DraftsScreen

const SegmentedContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin: 16px;` : `margin: 12px 24px 0px;`)}
`

const ScrollContainer = styled(ScrollView)`
  flex: 1;
  flex-direction: column;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '20px 25px')};
`

const List = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`}
`

const FormContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin-bottom: 8px;' : 'margin: 0px 16px 16px 0px;')};
  ${(props) => props.theme.breakpoint !== 'phone' && 'width: 360px;'};
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
